import React, { useEffect, useMemo, useState } from 'react';
import ReportDataTable, {
  ReportColumn,
} from 'shared/components/Table/ReportDataTable';
import AppIcon from 'shared/components/AppIcon';
import DrillBreadcrumb from 'shared/components/Dashboard/DrillBreadcrumb';
import {
  Filters,
  EMPTY_FILTERS,
  scopeLabel,
  withUdise,
} from 'shared/json/drilldown';
import {
  useGetSchoolInfraCautionSummaryQuery,
  useGetSchoolInfraCautionListQuery,
  useGetCommonMastersQuery,
} from './Dashboard.service';

import './InfrastructureCaution.scss';

interface InfrastructureCautionProps {
  /** Selected source (EMIS / UDISE+) — shown next to the title. */
  source?: 'emis' | 'udise';
  /** Academic year id. */
  acdYr?: number | string | null;
  /** Header geography scope. */
  districtId?: number | string | null;
  blockId?: number | string | null;
  /** Header geography names, for the drill breadcrumb. */
  district?: string | null;
  block?: string | null;
  /** Header-selected school name, for the "Caution — <SCHOOL>" heading. */
  school?: string | null;
  /** School id already resolved by Infrastructure.tsx (studentchildcountallyear
   * id space — NOT the header dropdown's schoollist id), used to scope both
   * caution queries to that one school. */
  schoolId?: number | null;
  /** UDISE of the resolved school, in case the caution endpoints key by it
   * instead of / alongside school_id. */
  schoolUdise?: string | null;
  /** Clear/return to a level — drives the breadcrumb's Back and crumbs. */
  onDistrictChange?: (value: string) => void;
  onBlockChange?: (value: string) => void;
  onSchoolChange?: (value: string) => void;
  /**
   * Focus the dashboard on one school, setting District + Block + School
   * together. Used by the flagged-school row click, which jumps straight to
   * a school — the row already carries its own district/block names, so all
   * three can be set in one call instead of three cascading handlers (each of
   * which resets its children and would wipe the others back to null).
   */
  onGeoSelect?: (geo: {
    district?: string | null;
    block?: string | null;
    school?: string | null;
  }) => void;
  /**
   * Report the Block / School dropdown options the HEADER should show while
   * this tab is active: only the blocks/schools that actually have a
   * flagged school for the currently selected check (matches Caution.tsx's
   * pattern). The dashboard overrides its header dropdowns with these, and
   * reverts to the normal full lists when this component unmounts (the
   * cleanup effect below calls it with null).
   */
  onScopeOptions?: (
    opts: {
      blocks: { label: string; value: string }[];
      schools: { label: string; value: string }[];
    } | null
  ) => void;
  /** Classification filters from the dashboard header. */
  filters?: Filters;
}

const num = (v: unknown) => Number(v) || 0;

// Caution key -> infrastructure type icon (using FontAwesome icons like the sidebar)
const ICON_FOR: Record<string, string> = {
  zero_classrooms: 'fa-solid fa-chalkboard', // Classrooms icon
  zero_toilets: 'fa-solid fa-toilet', // Toilets icon
  zero_urinals: 'fa-solid fa-restroom', // Urinals icon
  no_water: 'fa-solid fa-droplet', // Drinking Water icon
  water_source_not_functional: 'fa-solid fa-droplet', // Drinking Water icon
  no_drinking_water: 'fa-solid fa-droplet', // Drinking Water icon
  zero_furniture: 'fa-solid fa-chair', // Furniture icon
  data_not_submitted: 'fa-solid fa-clipboard-check', // Data entry icon
  no_safety: 'fa-solid fa-shield-halved', // Safety icon
};
const iconFor = (key: string) =>
  ICON_FOR[key] ?? 'fa-solid fa-triangle-exclamation';

/**
 * Severity per hard check. schoolinfracautionsummary's tiles carry no severity
 * field, so it is classified here.
 *
 * High = the school cannot function for that facility at all (no classroom, no
 * toilet, no water, or a water source that exists but is broken).
 * Medium = a real gap that still leaves the school usable, or a reporting gap
 * rather than a facility gap.
 */
const SEVERITY_FOR: Record<string, 'High' | 'Medium'> = {
  zero_classrooms: 'High',
  zero_toilets: 'High',
  no_water: 'High',
  no_drinking_water: 'High',
  water_source_not_functional: 'High',
  no_safety: 'High',
  zero_urinals: 'Medium',
  zero_furniture: 'Medium',
  data_not_submitted: 'Medium',
};
/** Unknown checks default to High so a new one is never under-reported. */
const severityFor = (key: string): 'High' | 'Medium' =>
  SEVERITY_FOR[key] ?? 'High';

/** High rows sort above Medium; API order is preserved within each group. */
const SEVERITY_RANK: Record<string, number> = { High: 0, Medium: 1 };

/**
 * Infrastructure Caution view (Infrastructure nav tab -> Caution sub-tab).
 *
 * Hard-check tiles (schoolinfracautionsummary) + a server-paginated detail
 * table of flagged schools for the selected tile (schoolinfracautionlist).
 */
const InfrastructureCaution: React.FC<InfrastructureCautionProps> = ({
  source = 'emis',
  acdYr = null,
  districtId = null,
  blockId = null,
  district = null,
  block = null,
  school = null,
  schoolId = null,
  schoolUdise = null,
  onDistrictChange,
  onBlockChange,
  onSchoolChange,
  onGeoSelect,
  onScopeOptions,
  filters = EMPTY_FILTERS,
}) => {
  const [activeType, setActiveType] = useState<string | null>(null);
  const [first, setFirst] = useState(0);

  // Current geography scope for the headings on this tab.
  const cautionScopeName = scopeLabel({ district, block, school });

  const acYr = acdYr != null ? Number(acdYr) : undefined;
  const geo = useMemo(
    () => ({
      ...(districtId != null ? { district_id: Number(districtId) } : {}),
      ...(blockId != null ? { block_id: Number(blockId) } : {}),
      ...(schoolId != null ? { school_id: Number(schoolId) } : {}),
      ...(schoolUdise ? { udise_code: schoolUdise } : {}),
      ...(filters.management[0] ? { manage_id: filters.management[0] } : {}),
      ...(filters.schoolType[0]
        ? { school_type_id: filters.schoolType[0] }
        : {}),
      ...(filters.category[0] ? { cate_id: filters.category[0] } : {}),
    }),
    [districtId, blockId, schoolId, schoolUdise, filters]
  );

  const { data: commonMasters } = useGetCommonMastersQuery();
  const manageMap = useMemo(() => {
    const m = new Map<number, string>();
    (commonMasters?.result?.manage_cate ?? []).forEach((x) => {
      if (x?.id != null) m.set(Number(x.id), String(x.manage_name ?? ''));
    });
    return m;
  }, [commonMasters]);

  // Tiles.
  const { data: summary, isFetching: summaryLoading } =
    useGetSchoolInfraCautionSummaryQuery({ ...geo });
  const tiles = useMemo(() => summary?.result?.tiles ?? [], [summary]);
  const totalSchools = num(summary?.result?.total_schools);

  // Default the selected tile to the first one once tiles load (derived — no effect).
  const activeTypeOrDefault = activeType ?? tiles[0]?.type ?? null;

  // Reset to page 1 whenever the tile or scope changes (render-phase reset).
  const scopeKey = `${activeTypeOrDefault}|${JSON.stringify(geo)}`;
  const [prevScopeKey, setPrevScopeKey] = useState(scopeKey);
  if (scopeKey !== prevScopeKey) {
    setPrevScopeKey(scopeKey);
    setFirst(0);
  }

  const selectedTile =
    tiles.find((t) => t.type === activeTypeOrDefault) ?? null;

  // Flagged-schools list — fetched ONCE per tile/scope, with no page /
  // page_size. The API returns the whole list in that single call, so the
  // table paginates client-side (below) and changing pages never refetches.
  const { data: listResp, isFetching: listLoading } =
    useGetSchoolInfraCautionListQuery(
      {
        type:
          activeTypeOrDefault != null ? Number(activeTypeOrDefault) : undefined,
        ...geo,
      },
      { skip: acYr == null || activeTypeOrDefault == null }
    );
  const listResult = listResp?.result;

  const detailRows = useMemo(() => {
    const norm = (s: string) => s.trim().toLowerCase();
    let rows = (listResult?.schools ?? []).map((r) => ({
      district: r.district_name ?? '',
      block: r.block_name ?? '',
      school: withUdise(r.school_name, r.udise_code),
      // Bare name used for header-dropdown selection on row click.
      _school_name: String(r.school_name ?? ''),
      // Raw ids kept for reliable client-side scoping below — matches
      // Caution.tsx's pattern (names from the API vary in case/format, ids
      // don't).
      districtRowId: Number(r.district_id ?? 0) || null,
      blockRowId: Number(r.block_id ?? 0) || null,
      management: manageMap.get(num(r.manage_id)) || String(r.manage_id ?? ''),
      students: num(r.students),
      finding: r.finding ?? '',
    }));

    // Defensive client-side narrowing, mirroring Caution.tsx (the working
    // tab): schoolinfracautionlist is ALSO sent district_id/block_id/
    // school_id/udise_code via `geo` above, but rather than trust that
    // backend scoping alone, re-filter on each row's own district_id/
    // block_id/school_name here too — so the table always matches the
    // header selection even if the school_id-level scoping upstream doesn't
    // narrow as expected, without firing a second request.
    if (districtId != null || district) {
      rows = rows.filter(
        (row) =>
          (districtId != null && row.districtRowId === Number(districtId)) ||
          (!!district && norm(row.district) === norm(district))
      );
    }
    if (blockId != null || block) {
      rows = rows.filter(
        (row) =>
          (blockId != null && row.blockRowId === Number(blockId)) ||
          (!!block && norm(row.block) === norm(block))
      );
    }
    if (school) {
      rows = rows.filter((row) => norm(row._school_name) === norm(school));
    }
    return rows;
  }, [listResult, manageMap, districtId, district, blockId, block, school]);

  // ── Header dropdown scoping ──────────────────────────────────────────────
  // While a district is selected here, the header's Block and School
  // dropdowns should list ONLY the blocks/schools that actually have a
  // flagged school for the currently selected check (not every block/school
  // in the district) — same behavior as the other tabs' Caution view.
  // Derived from the active check's own list (listResult), same scope the
  // grid below is already showing.
  const scopeBlocks = useMemo(() => {
    if (districtId == null) return [];
    const seen = new Set<string>();
    (listResult?.schools ?? []).forEach((r) => {
      const nm = String(r.block_name ?? '').trim();
      if (nm) seen.add(nm);
    });
    return Array.from(seen)
      .sort((a, b) => a.localeCompare(b))
      .map((nm) => ({ label: nm, value: nm }));
  }, [listResult, districtId]);

  const scopeSchools = useMemo(() => {
    if (districtId == null) return [];
    const seen = new Set<string>();
    (listResult?.schools ?? []).forEach((r) => {
      const nm = String(r.school_name ?? '').trim();
      if (nm) seen.add(nm);
    });
    return Array.from(seen)
      .sort((a, b) => a.localeCompare(b))
      .map((nm) => ({ label: nm, value: nm }));
  }, [listResult, districtId]);

  useEffect(() => {
    // Only override once a district is selected; otherwise let the header
    // use its normal lists.
    if (districtId == null) {
      onScopeOptions?.(null);
    } else {
      onScopeOptions?.({ blocks: scopeBlocks, schools: scopeSchools });
    }
    // Revert on unmount (leaving the Caution sub-tab).
    return () => onScopeOptions?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtId, scopeBlocks, scopeSchools]);

  const detailColumns: ReportColumn[] = [
    { key: 'district', label: 'District', type: 'text' },
    { key: 'block', label: 'Block', type: 'text' },
    // Link so the row is visually clickable and syncs the header school dropdown.
    { key: 'school', label: 'School', type: 'link' },
    { key: 'management', label: 'Management', type: 'muted' },
    { key: 'students', label: 'Students', type: 'number' },
    { key: 'finding', label: 'Finding', type: 'finding' },
  ];

  // All-checks summary table.
  const checksRows = useMemo(
    () =>
      tiles
        .map((t) => ({
          key: t.key,
          type: t.type,
          title: t.title,
          // `icon` is picked up by ReportDataTable's `link` cell renderer and
          // drawn before the check name.
          icon: iconFor(t.key),
          severity: severityFor(t.key),
          schools: num(t.schools),
          description: t.description,
        }))
        // Stable sort: High block first, then Medium, API order within each.
        .sort(
          (a, b) =>
            (SEVERITY_RANK[a.severity] ?? 99) -
            (SEVERITY_RANK[b.severity] ?? 99)
        ),
    [tiles]
  );
  const checksColumns: ReportColumn[] = [
    // `link` so the row gets its check icon (and the drill chevron, since the
    // table already selects a tile on row click).
    { key: 'title', label: 'Check', type: 'link' },
    { key: 'severity', label: 'Severity', type: 'badge' },
    { key: 'schools', label: 'Schools', type: 'number', bold: true },
    { key: 'description', label: 'What it means', type: 'muted' },
  ];

  const selectTile = (type: string) => {
    setActiveType(type);
    setFirst(0);
  };

  return (
    <div className="infra-caution">
      {/* -- Header -- */}
      <div className="infra-caution__head">
        <i className="pi pi-info-circle infra-caution__icon" />
        <h5 className="infra-caution__title">
          Caution - {cautionScopeName}
          <span className="infra-caution__subtitle">
            {' '}
            {totalSchools.toLocaleString('en-IN')} schools ·{' '}
            {source === 'emis' ? 'EMIS' : 'UDISE+'}
          </span>
        </h5>
      </div>

      {/* -- Info banner -- */}
      <div className="infra-caution__banner">
        <i className="pi pi-exclamation-circle" />
        <span>
          Schools failing a hard check - a facility or figure reported as zero.
          Pick a check to list the flagged schools; every list downloads in
          full.
        </span>
      </div>

      {/* -- Check tiles -- */}
      <div className="infra-caution__tiles">
        {tiles.map((t) => {
          const count = num(t.schools);
          const severity = count > 0 ? 'high' : 'low';
          return (
            <button
              key={t.key}
              type="button"
              className={`infra-caution__tile infra-caution__tile--${severity}${
                activeTypeOrDefault === t.type ? ' is-active' : ''
              }`}
              onClick={() => selectTile(t.type)}
            >
              <div className="infra-caution__tile-top">
                <span className="infra-caution__tile-icon">
                  <AppIcon icon={iconFor(t.key)} />
                </span>
                <span className="infra-caution__tile-count">
                  {count.toLocaleString('en-IN')}
                </span>
              </div>
              <span className="infra-caution__tile-title">{t.title}</span>
              <span className="infra-caution__tile-desc">{t.description}</span>
            </button>
          );
        })}
      </div>

      {/* -- Detail table for the selected check (server-paginated) -- */}
      <div className="infra-caution__block">
        <DrillBreadcrumb
          district={district}
          block={block}
          onDistrictChange={onDistrictChange}
          onBlockChange={onBlockChange}
          onSchoolChange={onSchoolChange}
        />
        <ReportDataTable
          title={selectedTile?.title ?? 'Flagged Schools'}
          hint={selectedTile?.description}
          icon={selectedTile ? iconFor(selectedTile.key) : 'pi pi-flag'}
          data={detailRows as unknown as Record<string, unknown>[]}
          loading={listLoading || summaryLoading}
          // columns={detailColumns}
          columns={
            detailRows.length === 1
              ? [
                  { ...detailColumns[0], type: 'text' },
                  ...detailColumns.slice(1),
                ]
              : detailColumns
          }
          dataKey="udise"
          exportFileName={`caution-${selectedTile?.key ?? 'schools'}`}
          searchable={false}
          rows={15}
          rowsPerPageOptions={[15, 30, 45]}
          // Client-side pagination: the full list is already in `detailRows`,
          // so no totalRecords / first / onPage (those drive server paging and
          // would refetch on every page change).
          first={first}
          onPage={setFirst}
          // Rows ARE schools (the leaf) — clicking one jumps straight to that
          // school, syncing the header District/Block/School dropdowns too
          // (the row already carries its own district/block names) so the
          // breadcrumb above this grid appears and matches the pick.
          onRowClick={(row) =>
            onGeoSelect?.({
              district: String(row.district ?? '') || null,
              block: String(row.block ?? '') || null,
              school: String(row._school_name ?? '') || null,
            })
          }
        />
      </div>

      {/* -- All checks summary table -- */}
      <div className="infra-caution__block cautions-grid">
        <ReportDataTable
          title={`All Caution Checks - ${cautionScopeName}`}
          hint="counts across every check"
          icon="pi pi-table"
          data={checksRows as unknown as Record<string, unknown>[]}
          loading={summaryLoading}
          columns={checksColumns}
          exportFileName="all-caution-checks"
          searchable={false}
          rows={12}
          onRowClick={(row) => selectTile(String(row.type))}
          // Mark the check currently shown in the detail table above.
          isRowActive={(row) =>
            String(row.type) === String(activeTypeOrDefault)
          }
        />
      </div>
    </div>
  );
};

export default InfrastructureCaution;
